import React, { useState, useEffect, useRef } from 'react';
import {
    Plus, Shield, Edit3, ChevronDown,
    Trash2, FileText, Layers, X, PlusCircle, Copy, Archive, CheckCircle2, AlertCircle, AlertTriangle,
    ArrowRight, ArrowLeft, Save, Building2, ShieldCheck, Users, Bell, Lock, List, Upload, ToggleRight, ToggleLeft
} from 'lucide-react';

const Settings = () => {
    // --- SETTINGS NAVIGATION STATE ---
    const [activeSettingTab, setActiveSettingTab] = useState('compliance');  //org-info

    // --- ORGANIZATION SETTINGS STATES (Based on Implementation Document) ---
    const [orgData, setOrgData] = useState({
        name: 'TecoreLabs',
        industry: 'FinTech',
        size: '50-100',
        contactName: 'Ben Carter',
        contactEmail: 'ben@tecorelabs.com',
        timezone: 'Asia/Kolkata',
        logo: null
    });

    const [complianceData, setComplianceData] = useState({
        defaultFramework: 'SOC2 Type II',
        auditCycle: 'Annual',
        owner: 'Security Team'
    });

    const [userAccessData, setUserAccessData] = useState({
        defaultRole: 'Coordinator',
        allowInvitations: false,
        inactiveDeactivation: '90 days'
    });

    const [notificationsData, setNotificationsData] = useState({
        auditorComment: true,
        evidenceUploaded: true,
        controlStatusChanged: true,
        gapCreated: true,
        reportGenerated: true
    });

    const [securityData, setSecurityData] = useState({
        allowedTypes: ['PDF', 'DOCX', 'XLSX', 'PNG'],
        maxUploadSize: '50 MB',
        retention: '7 Years',
        minPasswordLength: 12,
        twoFactor: true,
        sessionTimeout: '30 minutes'
    });

    const auditLogs = [
        { id: 1, date: '12 Mar 2024, 10:45 AM', user: 'Ben Carter', action: 'Evidence Uploaded', module: 'Controls', detail: 'CC6.1' },
        { id: 2, date: '12 Mar 2024, 11:20 AM', user: 'Alex Klein', action: 'Comment Added', module: 'Controls', detail: 'CC7.1' },
        { id: 3, date: '11 Mar 2024, 09:15 AM', user: 'Cynthia Patel', action: 'Status Changed', module: 'Frameworks', detail: 'SOC2 Type II marked Active' },
        { id: 4, date: '10 Mar 2024, 04:30 PM', user: 'System', action: 'Report Generated', module: 'Reports', detail: 'Q1 Compliance Summary' },
    ];

    const handleSaveSettings = () => {
        alert("Settings saved successfully!");
    };

    // --- EXISTING FRAMEWORKS & CONTROLS STATES ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewingFramework, setViewingFramework] = useState(null);
    const [showControlsTable, setShowControlsTable] = useState(false);
    const [frameworkToDelete, setFrameworkToDelete] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);


    const fileInputRef = useRef(null);

    // Handle the file selection and convert it to a previewable URL
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Update the state with the new image preview URL
                setOrgData(prev => ({ ...prev, logo: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const defaultControl = {
        id: Date.now().toString(),
        category: '',
        title: '',
        scopeRef: '',
        complianceRef: '',
        globalStandard: '',
        controlExpected: '',
        parameters: '',
        evidence: '',
        owner: '',
        status: 'Not Started'
    };

    const [newCategoryControls, setNewCategoryControls] = useState([{ ...defaultControl }]);
    const [controls, setControls] = useState([{ ...defaultControl }]);

    const [frameworks, setFrameworks] = useState([
        {
            id: '1',
            name: 'SOC 2 Type II',
            description: 'Security compliance framework',
            type: 'standard',
            version: 'SOC2 2017',
            owner: 'Compliance Team',
            projects: ['Alpha Dashboard'],
            status: 'Active',
            tags: ['Standard', 'Security', 'Active'],
            progress: 65,
            issuesCount: 3,
            passingCount: 45,
            categories: [
                {
                    id: 'cat-1',
                    name: 'Security Domain',
                    description: 'Core security and access controls mapped to SOC2.',
                    controls: [
                        { id: 'c1', title: 'Logical Access', scopeRef: 'All Systems', complianceRef: 'CC6.1', globalStandard: 'TSC 2017', controlExpected: 'Logical access controls must be in place to prevent unauthorized access.', parameters: 'In-Scope Only', evidence: 'Access logs, Access review matrix', owner: 'DevOps', status: 'Passing' },
                    ]
                }
            ]
        },
        {
            id: '2',
            name: 'ISO 27001',
            description: 'Information security management system standard.',
            type: 'standard',
            version: '2022',
            owner: 'Security Team',
            projects: ['All Projects'],
            status: 'Draft',
            tags: ['Standard', 'Security', 'Draft'],
            progress: 0,
            issuesCount: 0,
            passingCount: 0,
            categories: [
                {
                    id: 'cat-2',
                    name: 'Information Classification',
                    description: 'Policies for handling and labeling data.',
                    controls: [
                        { id: 'c3', title: 'Data Labeling', scopeRef: 'EU Customer Data', complianceRef: 'A.8.1', globalStandard: 'ISO 27001', controlExpected: 'Information must be classified.', parameters: 'All Information Assets', evidence: 'Classification policy', owner: 'Security', status: 'Not Started' }
                    ]
                }
            ]
        }
    ]);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'custom',
        version: '',
        owner: '',
        projects: '',
        status: 'Draft',
        category: ''
    });

    // --- EXISTING FRAMEWORK HANDLERS ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleControlChange = (id, field, value) => setControls(prev => prev.map(ctrl => ctrl.id === id ? { ...ctrl, [field]: value } : ctrl));
    const addControl = () => setControls(prev => [...prev, { ...defaultControl, id: Date.now().toString() + Math.random() }]);
    const removeControl = (id) => { if (controls.length > 1) setControls(prev => prev.filter(ctrl => ctrl.id !== id)); };

    const handleNewCategoryControlChange = (id, field, value) => setNewCategoryControls(prev => prev.map(ctrl => ctrl.id === id ? { ...ctrl, [field]: value } : ctrl));
    const addNewCategoryControl = () => setNewCategoryControls(prev => [...prev, { ...defaultControl, id: Date.now().toString() + Math.random() }]);
    const removeNewCategoryControl = (id) => { if (newCategoryControls.length > 1) setNewCategoryControls(prev => prev.filter(ctrl => ctrl.id !== id)); };

    const handleEditCategoryStart = (category, e) => {
        e.stopPropagation();
        setIsAddingCategory(true);
        setEditingCategoryId(category.id);
        setNewCategoryData({ name: category.name, description: category.description });
        setNewCategoryControls(JSON.parse(JSON.stringify(category.controls)));
    };

    const confirmDeleteCategory = (category, e) => { e.stopPropagation(); setCategoryToDelete(category); };

    const executeDeleteCategory = () => {
        if (!categoryToDelete || !viewingFramework) return;
        const updatedCategories = viewingFramework.categories.filter(c => c.id !== categoryToDelete.id);
        setViewingFramework({ ...viewingFramework, categories: updatedCategories });
        setFrameworks(prev => prev.map(fw => fw.id === viewingFramework.id ? { ...fw, categories: updatedCategories } : fw));
        setCategoryToDelete(null);
    };

    const handleSaveNewCategory = () => {
        if (!newCategoryData.name.trim() || !viewingFramework) return;
        let updatedCategories;
        if (editingCategoryId) {
            updatedCategories = viewingFramework.categories.map(cat =>
                cat.id === editingCategoryId ? { ...cat, name: newCategoryData.name, description: newCategoryData.description, controls: newCategoryControls } : cat
            );
        } else {
            const newCategory = { id: Date.now().toString() + '-cat', name: newCategoryData.name, description: newCategoryData.description, controls: newCategoryControls };
            updatedCategories = [...(viewingFramework.categories || []), newCategory];
        }
        setViewingFramework({ ...viewingFramework, categories: updatedCategories });
        setFrameworks(prev => prev.map(fw => fw.id === viewingFramework.id ? { ...fw, categories: updatedCategories } : fw));
        cancelCategoryEdit();
    };

    const cancelCategoryEdit = () => {
        setIsAddingCategory(false);
        setEditingCategoryId(null);
        setNewCategoryData({ name: '', description: '' });
        setNewCategoryControls([{ ...defaultControl }]);
    };

    const handleDuplicateFramework = (fw, e) => {
        e.stopPropagation();
        const newFramework = {
            ...fw,
            id: Date.now().toString(),
            name: `${fw.name} (Copy)`,
            status: 'Draft',
            progress: 0,
            passingCount: 0,
            issuesCount: 0,
            tags: [...fw.tags.filter(t => t !== 'Active' && t !== 'Archived' && t !== 'Draft'), 'Draft'],
            categories: JSON.parse(JSON.stringify(fw.categories || []))
        };
        setFrameworks([...frameworks, newFramework]);
    };

    const handleArchiveFramework = (fw, e) => {
        e.stopPropagation();
        setFrameworks(prev => prev.map(f => f.id === fw.id ? { ...f, status: 'Archived', tags: [...f.tags.filter(t => t !== 'Active' && t !== 'Draft' && t !== 'Archived'), 'Archived'] } : f));
    };

    const executeDeleteFramework = () => {
        if (!frameworkToDelete) return;
        setFrameworks(prev => prev.filter(fw => fw.id !== frameworkToDelete.id));
        setFrameworkToDelete(null);
    };

    const handleEditFramework = (fw, e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditingId(fw.id);
        setFormData({ name: fw.name, description: fw.description, type: fw.type, version: fw.version || '', owner: fw.owner || '', projects: fw.projects?.join(', ') || '', status: fw.status || 'Draft', category: '' });
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        const newTags = [formData.type === 'custom' ? 'Custom' : 'Standard', formData.status];

        if (isEditing) {
            setFrameworks(prev => prev.map(fw => fw.id === editingId ? { ...fw, ...formData, projects: formData.projects.split(',').map(p => p.trim()).filter(p => p), tags: newTags } : fw));
        } else {
            let generatedCategories = [];
            if (formData.type === 'standard') {
                const groups = controls.reduce((acc, ctrl) => {
                    const catName = ctrl.category?.trim() || 'Standard Domain';
                    if (!acc[catName]) acc[catName] = [];
                    const { category, ...cleanCtrl } = ctrl;
                    acc[catName].push(cleanCtrl);
                    return acc;
                }, {});
                generatedCategories = Object.entries(groups).map(([name, ctrls], idx) => ({ id: Date.now().toString() + `-cat-${idx}`, name: name, description: `Automatically grouped controls for ${name}`, controls: ctrls }));
            }
            else {
                generatedCategories = [{ id: Date.now().toString() + '-cat', name: formData.category || 'General Controls', description: 'Initial mapped controls', controls: controls }];
            }

            const newFramework = { id: Date.now().toString(), ...formData, projects: formData.projects.split(',').map(p => p.trim()).filter(p => p), tags: newTags, progress: 0, issuesCount: 0, passingCount: 0, categories: generatedCategories };
            setFrameworks([...frameworks, newFramework]);
        }
        closeModal();
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingId(null);
        setFormData({ name: '', description: '', type: 'custom', version: '', owner: '', projects: '', status: 'Draft', category: '' });
        setControls([{ ...defaultControl }]);
    };

    const closeViewingModal = () => {
        setViewingFramework(null);
        setShowControlsTable(false);
        cancelCategoryEdit();
        setSelectedCategory(null);
        setCategoryToDelete(null);
    };

    // --- REUSABLE TOGGLE COMPONENT ---
    const Toggle = ({ enabled, onChange }) => (
        <button type="button" onClick={onChange} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-indigo-600' : 'bg-slate-300'}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    );

    return (
        <div className="flex flex-col md:flex-row h-[calc(100vh-80px)] bg-slate-50/50">

            {/* SIDEBAR NAVIGATION */}
            <div className="w-full md:w-72 bg-white border-r border-slate-200 shrink-0 overflow-y-auto p-4 flex flex-col gap-1 custom-scrollbar">
                <div className="px-3 pb-2 mb-2 border-b border-slate-100">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Organization Setup</h3>
                </div>
                {/* <button onClick={() => setActiveSettingTab('org-info')} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'org-info' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Building2 size={18} /> Organization Info
                </button> */}
                <button 
                onClick={() => setActiveSettingTab('compliance')} 
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'compliance' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <ShieldCheck size={18} /> Compliance Config
                </button>
                <button 
                onClick={() => setActiveSettingTab('access')}
                 className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'access' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Users size={18} /> User Access
                </button>
                <button 
                onClick={() => setActiveSettingTab('notifications')}
                 className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'notifications' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Bell size={18} /> Notifications
                </button>
                <button 
                onClick={() => setActiveSettingTab('security')}
                 className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'security' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Lock size={18} /> Evidence & Security
                </button>
                <button 
                onClick={() => setActiveSettingTab('audit-log')}
                 className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeSettingTab === 'audit-log' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <List size={18} /> Audit Log
                </button>


            </div>

            {/* MAIN CONTENT AREA */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar pt-0">
                <div className="max-w-[1400px] mx-auto animate-in fade-in zoom-in-[0.98] duration-400 ease-out">

                    {/* --------------------------------------------------------- */}
                    {/* 1. ORGANIZATION INFORMATION */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'org-info' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <Building2 className="text-indigo-600" /> Organization Information
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Configure core company details used across compliance reports.</p>
                            </div>
                            <div className="bg-white p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-full mb-2">
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Organization Logo</label>
                                        <div className="flex items-center gap-6">
                                            <div className="w-24 h-24 rounded-2xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center text-slate-400 overflow-hidden">
                                                {orgData.logo ? (
                                                    <img src={orgData.logo} alt="Elora Logo" className="w-full h-full object-contain p-2" />
                                                ) : (
                                                    <Building2 size={32} />
                                                )}
                                            </div>

                                            {/* Hidden file input */}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={fileInputRef}
                                                onChange={handleLogoUpload}
                                                className="hidden"
                                            />

                                            {/* Button triggers the hidden file input click */}
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="px-4 py-2.5 bg-white ring-1 ring-slate-200 text-sm font-bold text-slate-600 rounded-xl hover:bg-slate-50 flex items-center gap-2 transition-all"
                                            >
                                                <Upload size={16} /> Upload Logo
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Organization Name</label>
                                        <input type="text" value={orgData.name} onChange={(e) => setOrgData({ ...orgData, name: e.target.value })} className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Industry</label>
                                        <div className="relative">
                                            <select value={orgData.industry} onChange={(e) => setOrgData({ ...orgData, industry: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['Technology', 'FinTech', 'Healthcare', 'E-commerce', 'SaaS', 'Finance', 'Education', 'Other'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Company Size</label>
                                        <div className="relative">
                                            <select value={orgData.size} onChange={(e) => setOrgData({ ...orgData, size: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['1-10', '10-50', '50-100', '100-500', '500+'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Primary Contact Name</label>
                                        <input type="text" value={orgData.contactName} onChange={(e) => setOrgData({ ...orgData, contactName: e.target.value })} className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Primary Contact Email</label>
                                        <input type="email" value={orgData.contactEmail} onChange={(e) => setOrgData({ ...orgData, contactEmail: e.target.value })} className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Timezone</label>
                                        <div className="relative">
                                            <select value={orgData.timezone} onChange={(e) => setOrgData({ ...orgData, timezone: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                                <option value="America/New_York">America/New_York (EST)</option>
                                                <option value="Europe/London">Europe/London (GMT)</option>
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                                    <button onClick={handleSaveSettings} className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2">
                                        <Save size={16} /> Save Information
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------------------------------------------------------- */}
                    {/* 2. COMPLIANCE CONFIGURATION */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'compliance' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <ShieldCheck className="text-indigo-600" /> Compliance Configuration
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Define default compliance settings applied to new projects.</p>
                            </div>
                            <div className="bg-white p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm max-w-3xl">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Default Framework</label>
                                        <div className="relative">
                                            <select value={complianceData.defaultFramework} onChange={(e) => setComplianceData({ ...complianceData, defaultFramework: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['SOC2 Type II', 'ISO 27001', 'HIPAA', 'GDPR'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-2 font-medium">Frameworks managed in Frameworks & Controls module.</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Audit Cycle</label>
                                        <div className="relative">
                                            <select value={complianceData.auditCycle} onChange={(e) => setComplianceData({ ...complianceData, auditCycle: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['Quarterly', 'Semi-Annual', 'Annual'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Compliance Owner (User)</label>
                                        <div className="relative">
                                            <select value={complianceData.owner} onChange={(e) => setComplianceData({ ...complianceData, owner: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                <option>Security Team</option>
                                                <option>Ben Carter</option>
                                                <option>Admin</option>
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                                    <button onClick={handleSaveSettings} className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2">
                                        <Save size={16} /> Save Configuration
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------------------------------------------------------- */}
                    {/* 3. USER ACCESS CONFIGURATION */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'access' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <Users className="text-indigo-600" /> User Access Configuration
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Define how users interact with the platform and manage inactivity.</p>
                            </div>
                            <div className="bg-white p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm max-w-3xl">
                                <div className="space-y-8">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Default Role for New Users</label>
                                        <div className="relative">
                                            <select value={userAccessData.defaultRole} onChange={(e) => setUserAccessData({ ...userAccessData, defaultRole: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['Admin', 'Coordinator', 'Auditor'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-2 font-medium">Specific roles are mapped in the Roles & Permissions module.</p>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl ring-1 ring-slate-200/60">
                                        <div>
                                            <h4 className="font-bold text-slate-800 text-sm">Allow User Invitations</h4>
                                            <p className="text-xs text-slate-500 mt-0.5">Allow standard users to invite other members to the platform.</p>
                                        </div>
                                        <Toggle enabled={userAccessData.allowInvitations} onChange={() => setUserAccessData(p => ({ ...p, allowInvitations: !p.allowInvitations }))} />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Inactive User Deactivation</label>
                                        <div className="relative">
                                            <select value={userAccessData.inactiveDeactivation} onChange={(e) => setUserAccessData({ ...userAccessData, inactiveDeactivation: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                {['30 days', '60 days', '90 days', 'Never'].map(opt => <option key={opt}>{opt}</option>)}
                                            </select>
                                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-2 font-medium">Automatically disable accounts if not logged in for this duration.</p>
                                    </div>
                                </div>
                                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                                    <button onClick={handleSaveSettings} className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2">
                                        <Save size={16} /> Save Access Rules
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------------------------------------------------------- */}
                    {/* 4. NOTIFICATION PREFERENCES */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'notifications' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <Bell className="text-indigo-600" /> Notification Preferences
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Configure automated alerts triggered by compliance and system events.</p>
                            </div>
                            <div className="bg-white rounded-3xl ring-1 ring-slate-200/60 shadow-sm overflow-hidden max-w-3xl">
                                <div className="p-0">
                                    <div className="grid grid-cols-1 divide-y divide-slate-100">

                                        <div className="flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 transition-colors">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Auditor Comment on Control</h4>
                                                <p className="text-xs text-slate-500 mt-1">Notify the Coordinator when an auditor leaves a comment.</p>
                                            </div>
                                            <Toggle enabled={notificationsData.auditorComment} onChange={() => setNotificationsData(p => ({ ...p, auditorComment: !p.auditorComment }))} />
                                        </div>

                                        <div className="flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 transition-colors">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Evidence Uploaded</h4>
                                                <p className="text-xs text-slate-500 mt-1">Notify the Auditor when new evidence is attached to a control.</p>
                                            </div>
                                            <Toggle enabled={notificationsData.evidenceUploaded} onChange={() => setNotificationsData(p => ({ ...p, evidenceUploaded: !p.evidenceUploaded }))} />
                                        </div>

                                        <div className="flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 transition-colors">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Control Status Changed</h4>
                                                <p className="text-xs text-slate-500 mt-1">Notify the Admin when a control passes or fails evaluation.</p>
                                            </div>
                                            <Toggle enabled={notificationsData.controlStatusChanged} onChange={() => setNotificationsData(p => ({ ...p, controlStatusChanged: !p.controlStatusChanged }))} />
                                        </div>

                                        <div className="flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 transition-colors">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Gap Created</h4>
                                                <p className="text-xs text-slate-500 mt-1">Notify the Coordinator when a missing requirement gap is raised.</p>
                                            </div>
                                            <Toggle enabled={notificationsData.gapCreated} onChange={() => setNotificationsData(p => ({ ...p, gapCreated: !p.gapCreated }))} />
                                        </div>

                                        <div className="flex items-center justify-between p-6 sm:p-8 hover:bg-slate-50/50 transition-colors">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Report Generated</h4>
                                                <p className="text-xs text-slate-500 mt-1">Notify the Admin when an exportable compliance report is ready.</p>
                                            </div>
                                            <Toggle enabled={notificationsData.reportGenerated} onChange={() => setNotificationsData(p => ({ ...p, reportGenerated: !p.reportGenerated }))} />
                                        </div>

                                    </div>
                                </div>
                                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                                    <button onClick={handleSaveSettings} className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2">
                                        <Save size={16} /> Save Notifications
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------------------------------------------------------- */}
                    {/* 5. EVIDENCE & SECURITY SETTINGS */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'security' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <Lock className="text-indigo-600" /> Evidence & Security Settings
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Define parameters for data upload, retention, and platform login security.</p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">

                                {/* Evidence Card */}
                                <div className="bg-white p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm flex flex-col">
                                    <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                        <Upload size={16} className="text-indigo-500" /> Evidence Configuration
                                    </h3>
                                    <div className="space-y-6 flex-1">
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-3">Allowed File Types</label>
                                            <div className="flex flex-wrap gap-3">
                                                {['PDF', 'DOCX', 'XLSX', 'PNG', 'JPG', 'CSV'].map(ext => (
                                                    <button
                                                        key={ext}
                                                        onClick={() => {
                                                            setSecurityData(p => ({
                                                                ...p,
                                                                allowedTypes: p.allowedTypes.includes(ext) ? p.allowedTypes.filter(t => t !== ext) : [...p.allowedTypes, ext]
                                                            }))
                                                        }}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${securityData.allowedTypes.includes(ext) ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-500'}`}
                                                    >
                                                        {ext}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Max File Upload Size</label>
                                            <div className="relative">
                                                <select value={securityData.maxUploadSize} onChange={(e) => setSecurityData({ ...securityData, maxUploadSize: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                    {['10 MB', '25 MB', '50 MB', '100 MB'].map(opt => <option key={opt}>{opt}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Evidence Retention Period</label>
                                            <div className="relative">
                                                <select value={securityData.retention} onChange={(e) => setSecurityData({ ...securityData, retention: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                    {['1 Year', '3 Years', '5 Years', '7 Years', 'Indefinite'].map(opt => <option key={opt}>{opt}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Security Card */}
                                <div className="bg-white p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm flex flex-col">
                                    <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                                        <ShieldCheck size={16} className="text-indigo-500" /> Platform Security
                                    </h3>
                                    <div className="space-y-6 flex-1">
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Minimum Password Length</label>
                                            <input type="number" min="8" max="32" value={securityData.minPasswordLength} onChange={(e) => setSecurityData({ ...securityData, minPasswordLength: e.target.value })} className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                        </div>

                                        <div className="flex items-center justify-between py-2">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-sm">Two-Factor Authentication (MFA)</h4>
                                                <p className="text-xs text-slate-500 mt-0.5">Enforce MFA for all organization users.</p>
                                            </div>
                                            <Toggle enabled={securityData.twoFactor} onChange={() => setSecurityData(p => ({ ...p, twoFactor: !p.twoFactor }))} />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Session Timeout</label>
                                            <div className="relative">
                                                <select value={securityData.sessionTimeout} onChange={(e) => setSecurityData({ ...securityData, sessionTimeout: e.target.value })} className="w-full appearance-none bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer">
                                                    {['15 minutes', '30 minutes', '1 hour', '4 hours'].map(opt => <option key={opt}>{opt}</option>)}
                                                </select>
                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            </div>
                                            <p className="text-xs text-slate-500 mt-2 font-medium">Automatically log out users after inactivity.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="max-w-5xl flex justify-end">
                                <button onClick={handleSaveSettings} className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:bg-indigo-700 active:scale-95 transition-all flex items-center gap-2">
                                    <Save size={16} /> Save Security Settings
                                </button>
                            </div>
                        </div>
                    )}

                    {/* --------------------------------------------------------- */}
                    {/* 6. AUDIT LOG */}
                    {/* --------------------------------------------------------- */}
                    {activeSettingTab === 'audit-log' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1 flex items-center gap-3">
                                    <List className="text-indigo-600" /> System Audit Log
                                </h2>
                                <p className="text-sm font-medium text-slate-500">Track all system activities for complete compliance traceability.</p>
                            </div>
                            <div className="bg-white ring-1 ring-slate-200/60 rounded-3xl overflow-hidden shadow-sm">
                                <div className="overflow-x-auto custom-scrollbar">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50/80 border-b border-slate-200">
                                                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-wider">Date</th>
                                                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-wider">User</th>
                                                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-wider">Action</th>
                                                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-wider">Module</th>
                                                <th className="py-4 px-6 text-xs font-black text-slate-400 uppercase tracking-wider">Details</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {auditLogs.map((log) => (
                                                <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="py-4 px-6 text-sm font-semibold text-slate-600 whitespace-nowrap">{log.date}</td>
                                                    <td className="py-4 px-6 text-sm font-bold text-slate-900">{log.user}</td>
                                                    <td className="py-4 px-6 text-sm font-bold text-indigo-600 bg-indigo-50/50">{log.action}</td>
                                                    <td className="py-4 px-6 text-sm font-medium text-slate-600">{log.module}</td>
                                                    <td className="py-4 px-6 text-sm font-medium text-slate-800">{log.detail}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}



                </div>
            </div>
        </div>
    );
};

export default Settings;